import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '../../components/ui/dialog';
import { Truck, Plus, Trash, Key } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriversAdminPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(null); // driver id
  const [newPw, setNewPw] = useState('');

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/drivers`, { withCredentials: true });
      setDrivers(data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchDrivers(); }, []);

  const createDriver = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error('Nom, email et mot de passe (>=6) requis');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/drivers`, form, { withCredentials: true });
      toast.success('Livreur créé');
      setCreateOpen(false);
      setForm({ name: '', email: '', phone: '', password: '' });
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce livreur ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/drivers/${id}`, { withCredentials: true });
      toast.success('Supprimé');
      fetchDrivers();
    } catch { toast.error('Erreur'); }
  };

  const resetPassword = async () => {
    if (newPw.length < 6) { toast.error('>=6 caractères'); return; }
    try {
      await axios.put(`${API_URL}/api/admin/drivers/${pwOpen}`, { password: newPw }, { withCredentials: true });
      toast.success('Mot de passe mis à jour');
      setPwOpen(null);
      setNewPw('');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6" data-testid="drivers-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Truck size={26} /> Livreurs</h1>
          <p className="text-sm text-slate-500">Gérez les comptes livreurs ayant accès à la tournée du jour.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="add-driver-btn">
              <Plus size={16} className="mr-1" /> Nouveau livreur
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un compte livreur</DialogTitle>
              <DialogDescription>Le livreur se connectera sur /livreur/login</DialogDescription>
            </DialogHeader>
            <form onSubmit={createDriver} className="space-y-3">
              <div><Label>Nom complet *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="new-driver-name" /></div>
              <div><Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="new-driver-email" /></div>
              <div><Label>Téléphone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       data-testid="new-driver-phone" /></div>
              <div><Label>Mot de passe (≥6) *</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                       data-testid="new-driver-password" /></div>
              <Button type="submit" disabled={busy} className="w-full" data-testid="save-driver-btn">
                {busy ? 'Création…' : 'Créer le livreur'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Comptes livreurs ({drivers.length})</CardTitle>
          <CardDescription>L'app livreur est accessible sur <code>/livreur/login</code></CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-slate-500">Chargement…</p> : drivers.length === 0 ? (
            <p className="text-slate-500 text-sm py-6 text-center">Aucun livreur. Créez-en un pour démarrer.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-slate-500">
                  <th className="py-2 px-2">Nom</th>
                  <th className="py-2 px-2">Email</th>
                  <th className="py-2 px-2">Téléphone</th>
                  <th className="py-2 px-2">Statut</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((d) => (
                  <tr key={d.id} className="border-b hover:bg-slate-50" data-testid={`driver-row-${d.id}`}>
                    <td className="py-3 px-2 font-medium">{d.name}</td>
                    <td className="py-3 px-2">{d.email}</td>
                    <td className="py-3 px-2">{d.phone || '—'}</td>
                    <td className="py-3 px-2">
                      {d.is_active === false
                        ? <Badge variant="outline" className="border-red-300 text-red-700">Désactivé</Badge>
                        : <Badge className="bg-emerald-600">Actif</Badge>}
                    </td>
                    <td className="py-3 px-2 flex gap-1 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setPwOpen(d.id)}
                              data-testid={`reset-pw-${d.id}`}>
                        <Key size={14} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => remove(d.id)}
                              data-testid={`del-driver-${d.id}`}>
                        <Trash size={14} className="text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
          </DialogHeader>
          <Input value={newPw} onChange={(e) => setNewPw(e.target.value)}
                 placeholder="Nouveau mot de passe (≥6)" data-testid="reset-pw-input" />
          <Button onClick={resetPassword} className="w-full" data-testid="reset-pw-submit">Mettre à jour</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversAdminPage;
